import { TournamentState } from '@ps/web-poker-tile-mappers';
import classNames from 'clsx';
import React, { memo } from 'react';
import { useDispatch } from 'react-redux';
import type { UseTableInstanceProps } from 'react-table';
import type { ListChildComponentProps } from 'react-window';

import type { Dispatch } from '@swf/core/store';

import type { TableColumnInstance, Tournament } from '../../../types/MTT';
import { ModalPath } from '../../../types/common';
import sharedStyles from '../shared.scss';

import styles from './TableRow.scss';

export type ItemData = Pick<UseTableInstanceProps<Tournament>, 'rows' | 'prepareRow'> & {
  selectedRowId?: number;
  onRowClick?: (rowId: number) => void;
  onRowDoubleCLick?: (rowId: number) => void;
  classes?: { row?: string; cell?: string };
};

const TableRow: React.FC<ListChildComponentProps<ItemData>> = memo(({ index, style, data }) => {
  const { modal } = useDispatch<Dispatch>();
  const { rows, prepareRow, selectedRowId, onRowClick, onRowDoubleCLick, classes } = data;
  const row = rows[index];
  const rowId = row.original.id;
  const isLateRegState = row.original.formattedState === TournamentState.LATE_REGISTRATION;
  const isRowDisabled = !row.original.isEnabled;
  const isRowSelected = selectedRowId === rowId && !isRowDisabled;

  prepareRow(row);

  const handleRowClick = () => {
    if (isRowSelected) return;
    if (isRowDisabled) {
      modal.enqueue({ path: ModalPath.VerifyAccount });
      return;
    }
    onRowClick?.(rowId);
  };
  const handleRowDoubleCLick = () => onRowDoubleCLick?.(rowId);
  const { key, ...rowProps } = row.getRowProps({ style });

  return (
    <div
      key={key}
      {...rowProps}
      className={classNames(styles.container, classes?.row || '', {
        [styles.rowHover]: !isRowSelected,
        [styles.rowGrey]: !!(index % 2),
        [styles.rowSelected]: isRowSelected,
        [styles.rowDisabled]: isRowDisabled
      })}
      onClick={handleRowClick}
      onDoubleClick={handleRowDoubleCLick}
      data-rowid={rowId}
    >
      {row.cells.map((cell) => {
        const column = cell.column as TableColumnInstance;
        const { key, ...cellProps } = cell.getCellProps();

        return (
          <div
            key={key}
            {...cellProps}
            className={classNames(
              styles.cellContainer,
              classes?.cell || '',
              {
                [sharedStyles.cellExpand]: column.cellExpand,
                [styles.rowActive]: isRowSelected,
                [String(column?.lateRegStyle)]: column?.lateRegStyle && isLateRegState
              },
              column?.cellStyles
            )}
            style={{ width: column?.width }}
          >
            {cell.render('Cell')}
          </div>
        );
      })}
    </div>
  );
});

export default TableRow;
